import { appInsights, openReplayTracker } from "@/main";
import * as Sentry from "@sentry/browser";
import posthog from "posthog-js";
import { useUser } from "@/store/pinia/userStore";
import { Capacitor } from "@capacitor/core";
import Analytics from "@capacitor-community/appcenter-analytics";
import { AnalyticsBrowser } from "@segment/analytics-next";
import { MembershipRole } from "@/models/enums/Role";
import formbricks from "@formbricks/js";
import { nrAgent } from "./NewRelicModule";
import { env } from "@/env";

export const segmentAnalytics = new AnalyticsBrowser();
let formbricksInitialized = false;

export const isFormbricksInitialized = () => formbricksInitialized;

export class WebAnalytics {
	private static dataLayerName = "dataLayer";

	private static getHighestRole(): string {
		const user = useUser();
		const currentMemberships = user?.memberships ?? null;
		if (!currentMemberships) return "";

		const roles: MembershipRole[] = [...(currentMemberships.productions ?? []).map((m) => m.role), ...(currentMemberships.organizations ?? []).map((m) => m.role)];

		if (roles.length === 0) return "";

		// Most-privileged role is the lowest numeric enum value (Owner=0 ... Member=3)
		const mostPrivilegedRole = Math.min(...roles) as MembershipRole;

		return MembershipRole[mostPrivilegedRole] ?? "";
	}

	static identify(identity: AnalyticsIdentity) {
		if (env.ANALYTICS === "disabled") {
			return;
		}
		segmentAnalytics.identify(identity.id, identity);
		nrAgent?.setUserId(identity.id);

		appInsights.setAuthenticatedUserContext(identity.id, undefined, true);
		Sentry.setUser({
			id: identity.id,
			email: identity.email,
			username: `${identity.firstName} ${identity.lastName}`,
		});
		Sentry.setTag("role", this.getHighestRole());
		posthog.identify(identity.id, {
			...identity,
			role: this.getHighestRole(),
		});
		openReplayTracker.setUserID(identity.email);
		openReplayTracker.setMetadata("Role", this.getHighestRole());

		try {
			if (typeof window !== "undefined") {
				formbricksInitialized = true;
				formbricks.init({
					environmentId: env.FORMBRICKS_ENV_ID,
					apiHost: env.FORMBRICKS_API_HOST,
					userId: identity.id,
					attributes: {
						Role: this.getHighestRole(),
						email: identity.email,
						"First Name": identity.firstName,
						"Last Name": identity.lastName,
						Industry: identity.industry,
						"Initial Persona": identity.initialPersona,
						"User Type": identity.userType,
					},
				});
			}
		} catch (err) {
			formbricksInitialized = false;
		}
	}

	static identifyOrganization(identity: OrganizationIdentity) {
		if (env.ANALYTICS === "disabled") {
			return;
		}
		segmentAnalytics.group(identity.organizationId, identity);
		// WebAnalytics.pushToDataLayer(this.groupEventName, {
		// 	groupId: identity.organizationId,
		// 	groupObject: identity,
		// });
		posthog.group("organization", identity.organizationId, identity);
	}

	static identifyProduction(identity: ProductionIdentity) {
		if (env.ANALYTICS === "disabled") {
			return;
		}
		segmentAnalytics.group(identity.productionId, identity);
		// WebAnalytics.pushToDataLayer(this.groupEventName, {
		// 	groupId: identity.productionId,
		// 	groupObject: identity,
		// });
		posthog.group("production", identity.productionId, identity);
	}

	static logout() {
		if (env.ANALYTICS === "disabled") {
			return;
		}
		segmentAnalytics.reset();
		appInsights.clearAuthenticatedUserContext();
		posthog.reset();
		Sentry.setUser(null);
		formbricksInitialized = false;
		formbricks.logout();
	}

	static view(page: PageSpec) {
		if (env.ANALYTICS === "disabled") {
			return;
		}
		page.platform = Capacitor.getPlatform();
		page.application = "Backstage";
		segmentAnalytics.page(page.pageName, page);
		// WebAnalytics.pushToDataLayer(this.pageViewEventName, page);
		appInsights.trackPageView({
			name: page.pageName,
			refUri: `${document.location.protocol}://${document.location.host}${page.fromPath}`,
			uri: `${document.location.protocol}://${document.location.host}${page.path}`,
		});
		posthog.capture("$pageview");
		openReplayTracker.event("view", page.pageName);
	}

	static track(event: AnalyticsEvent, payload: DataLayerSpec) {
		if (env.ANALYTICS === "disabled") {
			return;
		}
		segmentAnalytics.track(event, { ...payload, application: "Backstage", platform: Capacitor.getPlatform() });
		posthog.capture(event, payload);
		openReplayTracker.event(event, payload);
		appInsights.trackEvent({ name: event, properties: payload });
		if (Capacitor.isNativePlatform()) {
			Analytics.trackEvent({ name: event });
		}
	}

	static trackFlexible(event: string, payload: any) {
		if (env.ANALYTICS === "disabled") {
			return;
		}
		payload.platform = Capacitor.getPlatform();
		payload.application = "Backstage";
		nrAgent?.recordCustomEvent(event, payload);
		segmentAnalytics.track(event, payload);
		posthog.capture(event, payload);
		openReplayTracker.event(event, payload);
		appInsights.trackEvent({ name: event, properties: payload });
		if (Capacitor.isNativePlatform()) {
			Analytics.trackEvent({ name: event, properties: payload });
		}
	}

	/* Helpers */
	/**
	 * Push Payload to DataLayer
	 * @description Will prepend the trigger event to the data layer payload, and push to the window object specified in {dataLayerName}
	 * @param event Trigger event in tag manager
	 * @param payload The object to send
	 * @private
	 * @deprecated
	 */
	private static pushToDataLayer(event: string, payload?: object) {
		if (env.SH_ENV === "disabled") {
			return;
		}
		const payloadToSend = payload ? payload : {};
		payloadToSend["event"] = event;
		if (typeof window[this.dataLayerName] !== "undefined") {
			window[this.dataLayerName].push(payloadToSend);
		}
	}
}

/***
 * Analytics Identity
 * @description This is the identity object that will be sent to all analytics platforms
 * @see https://segment.com/docs/connections/spec/identify/
 */
interface AnalyticsIdentity {
	id: string;
	age: number;
	avatar: string;
	$avatar: string;
	birthday: string;
	createdAt: string;
	email: string;
	firstName: string;
	lastName: string;
	name: string;
	gender: string;
	title: string;
	pronouns: string;
	StageCode: string;
	industry: string;
	initialPersona: string;
	userType: string;
}

interface PageSpec {
	pageName: string;
	path: string;
	url: string;
	fromPath: string;
	friendlyPath: string;
	friendlyUrl: string;
	platform: string;
	application: string;
}

interface DataLayerSpec {
	eventName: AnalyticsEvent;
	wizardName?: AnalyticsWizard;
	wizardStep?: string;
	entity?: AnalyticsEntity;
	action?: AnalyticsAction;
	profileEntity?: AnalyticsProfileEntity;
	//eventSource?: AnalyticsEventSource
	//linkName?: string
}

export enum AnalyticsEvent {
	Wizard = "Wizard",
	ManageEntity = "Manage Entity",
	ManageProfile = "Manage Profile",
	Login = "Login",
	Logout = "Logout",
	LinkClick = "Link Click",
}

export enum AnalyticsEntity {
	Communication = "Communication",
	Announcement = "Announcement",
	Notification = "Notification",
	Event = "Event",
	Call = "Call",
	Profile = "User Profile",
	OrgProfile = "Organization Profile",
	ProdProfile = "Production Profile",
	Report = "Report",
}

export enum AnalyticsProfileEntity {
	Venue = "Venue",
	VenueSpace = "Venue Space",
	Person = "Person",
	Group = "Group",
	Conflict = "Conflict",
	CallType = "CallType",
	ReportTemplate = "Report Template",
	EmailAddress = "Email Address",
	MailingAddress = "Mailing Address",
	PhoneNumber = "Phone Number",
	EmergencyContact = "Emergency Contact",
	MedicalNote = "Medical Note",
	PrivacySetting = "Privacy Setting",
	Password = "Password",
	GeneralSetting = "General Setting",
	Photo = "Photo",
}

export enum AnalyticsAction {
	Create = "Create",
	Read = "Read",
	Update = "Update",
	Delete = "Delete",
	Start = "Start",
	Complete = "Complete",
	Publish = "Publish",
	Verify = "Verify",
}

export enum AnalyticsWizard {
	SignUp = "Sign Up",
	ForgotPassword = "Forgot Password",
	CreateProfile = "Create Profile",
	CreateOrganization = "Create Organization",
	CreateProduction = "Create Production",
}

export enum AnalyticsEventSource {
	QuickActions = "Quick Actions",
	Dashboard = "Dashboard",
}

export interface OrganizationIdentity {
	organizationId: string;
	name: string;
	$name: string;
	$avatar: string;
	//createdAt: string
	email: string;
	phone: string;
	//employees: number
	type: string; //Industry
	website: string;
	//productionCount: number
	StageCode: string;
	industry: string;
}

export interface ProductionIdentity {
	productionId: string;
	name: string;
	$name: string;
	$avatar: string;
	//createdAt: string
	//employees: number
	type: string;
	//website: string
	StageCode: string;
}
